import { motion } from "framer-motion";
import { IMG } from "@/components/data";
import { useLang } from "@/i18n";
import { Package, Boxes, Layers, Truck, ArrowUpRight } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] } }),
};

const ICONS = [Package, Boxes, Layers, Truck];

function Format({ icon: Icon, tag, title, desc, className, image, dark, i }) {
  return (
    <motion.div
      custom={i}
      variants={reveal}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      data-testid={`format-card-${i}`}
      className={`group relative border border-ink/80 overflow-hidden rounded-2xl ${className} ${dark ? "bg-ink text-white" : "bg-white text-ink"}`}
    >
      {image && (
        <div className="absolute inset-0">
          <img src={image} alt={title} className="w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-700" />
          <div className="absolute inset-0 bg-ink/40" />
        </div>
      )}
      <div className="relative p-7 md:p-9 h-full flex flex-col justify-between min-h-[220px]">
        <div className="flex items-start justify-between">
          <Icon size={30} className={image ? "text-white" : "text-ferrari"} strokeWidth={1.6} />
          <ArrowUpRight size={22} className={`opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-300 ${image ? "text-white" : "text-ferrari"}`} />
        </div>
        <div>
          <span className={`text-[11px] font-bold uppercase tracking-[0.2em] ${image ? "text-white/70" : "text-ferrari"}`}>{tag}</span>
          <h3 className="font-display font-extrabold uppercase tracking-tight text-2xl md:text-3xl mt-2 leading-none">{title}</h3>
          <p className={`mt-3 text-sm leading-relaxed ${image ? "text-white/80" : "text-ink/60"}`}>{desc}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function Products() {
  const { t } = useLang();
  const cards = t.products.cards;
  return (
    <section id="prodotti" className="pt-[50px] pb-20 md:pt-[82px] md:pb-28 bg-white">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-ferrari">{t.products.label}</span>
            <h2 className="font-display font-extrabold uppercase tracking-tighter text-4xl md:text-6xl mt-4 leading-[0.9]">
              {t.products.titleA}<span className="text-ferrari">{t.products.titleB}</span>
            </h2>
          </div>
          <p className="max-w-sm text-ink/60 text-sm md:text-base leading-relaxed">{t.products.intro}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4">
          {cards.map((c, i) => (
            <Format
              key={i}
              i={i}
              icon={ICONS[i]}
              tag={c.tag}
              title={c.title}
              desc={c.desc}
              className={i === 3 ? "md:col-span-3" : ""}
              image={i === 3 ? IMG.truck : undefined}
              dark={i === 3}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
